import React, { useState, useEffect, useCallback } from 'react';
import Papa from 'papaparse';

export default function FleetInfoModal({ isOpen, onClose }) {
  const [fleetData, setFleetData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('registration'); // registration, model, age
  const [sortOrder, setSortOrder] = useState('asc');
  const [configPopup, setConfigPopup] = useState(null); // { index, x, y }

  const parseAge = (ageString) => {
    if (!ageString) return '';
    
    // Try to extract years from strings like "7.3 Years" or "7 Years"
    const match = ageString.match(/(\d+\.?\d*)\s*[Yy]ear/);
    if (match) {
      const years = parseFloat(match[1]);
      // Round to nearest 0.5
      const rounded = Math.round(years * 2) / 2;
      return `${rounded} ${rounded === 1 ? 'year' : 'years'}`;
    }
    
    // If no match, return the original string
    return ageString;
  };

  const fetchFleetData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const sheetId = process.env.REACT_APP_FLEET_INFO_SHEET_ID;
      const url = `https://docs.google.com/spreadsheets/d/${sheetId}/export?format=csv`;
      
      const response = await fetch(url);
      const csvText = await response.text();
      
      Papa.parse(csvText, {
        header: true,
        dynamicTyping: true,
        skipEmptyLines: true,
        complete: (results) => {
          const processedData = results.data
            .filter(row => row['Aircraft Registration'])
            .map(row => ({
              registration: row['Aircraft Registration'] || '',
              model: row['Model'] || '',
              deliveryDate: row['Dt-td'] || '',
              configuration: row['Configuration'] || '',
              age: parseAge(row['Age']),
              planespottersUrl: row['URL'] || '',
              productionUrl: row['URL'] || '',
              correctionUrl: row['Correction-link URL'] || '',
              photoUrl: row['Airframe-photo-link URL'] || ''
            }));
          
          setFleetData(processedData);
          setLoading(false);
        },
        error: (err) => {
          console.error('Fleet CSV parsing error:', err);
          setError('Failed to parse fleet data');
          setLoading(false);
        }
      });
    } catch (err) {
      console.error('Failed to fetch fleet data:', err);
      setError('Failed to fetch fleet data');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchFleetData();
    }
  }, [isOpen, fetchFleetData]);

  useEffect(() => {
    const handleScroll = () => {
      setConfigPopup(null);
    };

    if (isOpen && configPopup) {
      const scrollContainer = document.querySelector('.fleet-info-scroll-container');
      if (scrollContainer) {
        scrollContainer.addEventListener('scroll', handleScroll);
        return () => scrollContainer.removeEventListener('scroll', handleScroll);
      }
    }
  }, [isOpen, configPopup]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const parseConfiguration = (config) => {
    if (!config) return null;
    
    // Parse configuration like "C67W94" or "F4C67W94"
    const parts = config.match(/([A-Z])(\d+)/g);
    if (!parts) return null;

    const cabinTypes = {
      'F': 'First Class',
      'C': 'Business Class',
      'W': 'Premium Economy',
      'Y': 'Economy Class'
    };

    const parsed = parts.map(part => {
      const match = part.match(/([A-Z])(\d+)/);
      if (match) {
        const type = match[1];
        const count = match[2];
        return {
          type,
          count: parseInt(count),
          label: cabinTypes[type] || type
        };
      }
      return null;
    }).filter(Boolean);

    return parsed;
  };

  const formatConfiguration = (config) => {
    const parsed = parseConfiguration(config);
    if (!parsed) return config;
    
    return parsed.map(p => `${p.type}${p.count}`).join(' / ');
  };

  const handleConfigClick = (e, index, config) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setConfigPopup({
      index,
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
      config
    });
  };

  const filteredAndSortedData = fleetData
    .filter(aircraft => {
      const search = searchTerm.toLowerCase();
      return (
        aircraft.registration.toLowerCase().includes(search) ||
        aircraft.model.toLowerCase().includes(search) ||
        aircraft.configuration.toLowerCase().includes(search)
      );
    })
    .sort((a, b) => {
      let comparison = 0;
      if (sortBy === 'registration') {
        comparison = a.registration.localeCompare(b.registration);
      } else if (sortBy === 'model') {
        comparison = a.model.localeCompare(b.model);
      } else if (sortBy === 'age') {
        const aA = parseFloat(a.age) || 0;
        const aB = parseFloat(b.age) || 0;
        comparison = aA - aB;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-[2000] transition-opacity"
        onClick={() => {
          setConfigPopup(null);
          onClose();
        }}
      />

      {/* Modal */}
      <div className="fixed inset-4 md:inset-8 lg:inset-12 bg-white rounded-2xl shadow-2xl z-[2001] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-800">Fleet Information</h2>
              <p className="text-sm text-gray-600">Singapore Airlines Aircraft Fleet</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search and Stats Bar */}
        <div className="p-4 md:px-6 md:pb-4 border-b border-gray-200 bg-gray-50">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="relative flex-1 w-full sm:max-w-md">
              <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search by registration, model, or config..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm">
                <span className="font-semibold text-gray-700">{filteredAndSortedData.length}</span>
                <span className="text-gray-500"> aircraft</span>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4 md:p-6 fleet-info-scroll-container" onClick={() => setConfigPopup(null)}>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading fleet data...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-red-600 font-semibold mb-2">{error}</p>
                <button
                  onClick={fetchFleetData}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    <th 
                      onClick={() => handleSort('registration')}
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors"
                    >
                      <div className="flex items-center space-x-1">
                        <span>Registration</span>
                        {sortBy === 'registration' && (
                          <svg className={`w-4 h-4 transition-transform ${sortOrder === 'desc' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                          </svg>
                        )}
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('model')}
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors"
                    >
                      <div className="flex items-center space-x-1">
                        <span>Model</span>
                        {sortBy === 'model' && (
                          <svg className={`w-4 h-4 transition-transform ${sortOrder === 'desc' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                          </svg>
                        )}
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Delivery
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Configuration
                    </th>
                    <th 
                      onClick={() => handleSort('age')}
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition-colors"
                    >
                      <div className="flex items-center space-x-1">
                        <span>Age</span>
                        {sortBy === 'age' && (
                          <svg className={`w-4 h-4 transition-transform ${sortOrder === 'desc' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                          </svg>
                        )}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredAndSortedData.map((aircraft, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="font-mono font-semibold text-blue-600">{aircraft.registration}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-700">
                        {aircraft.model}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-600 text-sm">
                        {aircraft.deliveryDate}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap relative">
                        <button
                          onClick={(e) => handleConfigClick(e, index, aircraft.configuration)}
                          className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-semibold rounded hover:bg-purple-200 transition-colors cursor-pointer"
                        >
                          {formatConfiguration(aircraft.configuration)}
                        </button>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-600 text-sm">
                        {aircraft.age}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {filteredAndSortedData.length === 0 && (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <p className="text-gray-500">No aircraft found matching your search</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Disclaimer Footer */}
        <div className="px-4 md:px-6 py-3 border-t border-gray-200 bg-gray-50">
          <p className="text-xs text-gray-500 text-center">
            Singapore Airlines fleet data was manually compiled from public sources and last updated in Jun 2026. This information is for reference only and may not reflect the latest details—please verify before use.
          </p>
        </div>
      </div>

      {/* Configuration Popup Tooltip */}
      {configPopup && (
        <div
          className="fixed z-[2002] bg-gray-900 text-white text-sm px-4 py-2 rounded-lg shadow-xl transform -translate-x-1/2 -translate-y-full pointer-events-none"
          style={{
            left: `${configPopup.x}px`,
            top: `${configPopup.y}px`,
            maxWidth: '300px'
          }}
        >
          <div className="space-y-1">
            {parseConfiguration(configPopup.config)?.map((item, idx) => (
              <div key={idx}>
                {item.count} {item.label} {item.count === 1 ? 'seat' : 'seats'}
              </div>
            ))}
          </div>
          {/* Arrow */}
          <div
            className="absolute left-1/2 transform -translate-x-1/2 top-full w-0 h-0"
            style={{
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: '6px solid #1f2937'
            }}
          />
        </div>
      )}
    </>
  );
}